import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import DashboardLayout from '@/components/layout/DashboardLayout';
import SecuritySettingsPanel from '@/components/security/SecuritySettingsPanel';
import GdprSettingsPanel from '@/components/settings/GdprSettingsPanel';
import NotificationPreferencesPanel from '@/components/settings/NotificationPreferencesPanel';
import type { NotificationCategory } from '@/components/settings/NotificationPreferencesPanel';
import SubscriptionPanel from '@/components/settings/SubscriptionPanel';
import type { SubscriptionPlanConfig } from '@/components/settings/SubscriptionPanel';
import PageHeader from '@/components/ui/PageHeader';
import { playerSidebar } from '@/config/sidebarConfig.ts';
import {
  updateMeApi,
  userStripeBillingPortalApi,
  userStripeSetupIntentApi,
  userStripeSubscribeApi,
} from '@/lib/api/usersApi';
import { useAuthStore } from '@/stores/auth-store';
import { useSaasStore } from '@/stores/saas-store';
import { JOUEUR_PLUS_ANNUAL_TTC_CENTS, JOUEUR_PLUS_MONTHLY_TTC_CENTS } from '@shared/constants';
import { isUserPremium } from '@shared/utils/premium.utils';
import { useMutation } from '@tanstack/react-query';
import { Bell, Check, Clock, Copy, CreditCard, Database, Eye, EyeOff, Gift, Globe, Shield } from 'lucide-react';
import { toast } from 'sonner';

// ─── Tab definitions ───────────────────────────────────────────

type TabId = 'security' | 'subscription' | 'referral' | 'notifications' | 'gdpr' | 'privacy' | 'language';

interface TabDef {
  id: TabId;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  soon?: true;
}

const TABS: TabDef[] = [
  {
    id: 'security',
    label: 'Securite',
    description: '2FA, appareils de confiance',
    icon: Shield,
  },
  {
    id: 'subscription',
    label: 'Abonnement',
    description: 'Plan Joueur Plus',
    icon: CreditCard,
  },
  {
    id: 'referral',
    label: 'Parrainage',
    description: 'Code parrainage et recompenses',
    icon: Gift,
  },
  {
    id: 'notifications',
    label: 'Notifications',
    description: 'Confirmations, rappels, resultats',
    icon: Bell,
  },
  {
    id: 'gdpr',
    label: 'Donnees personnelles',
    description: 'Export RGPD, suppression de compte',
    icon: Database,
  },
  {
    id: 'privacy',
    label: 'Confidentialite',
    description: 'Visibilite du profil',
    icon: Eye,
  },
  {
    id: 'language',
    label: 'Langue et region',
    description: 'Langue, fuseau horaire',
    icon: Globe,
    soon: true,
  },
];

const DEFAULT_TAB: TabId = 'security';

const PLAYER_NOTIFICATION_CATEGORIES: NotificationCategory[] = [
  {
    id: 'registration',
    label: "Confirmations d'inscription",
    description: 'Confirmation quand vous etes inscrit a un concours',
    email: true,
  },
  {
    id: 'reminders',
    label: 'Rappels de concours',
    description: 'Rappels avant le debut de vos concours',
    email: true,
  },
  {
    id: 'results',
    label: 'Resultats et classements',
    description: 'Scores, classements et statistiques apres vos concours',
    email: true,
  },
  {
    id: 'club_messages',
    label: 'Messages du club',
    description: 'Communications de votre club',
    email: false,
  },
  {
    id: 'promotions',
    label: 'Nouveautes et promotions',
    description: 'Concours a proximite, nouveautes plateforme',
    email: false,
  },
];

// ─── Settings Nav ──────────────────────────────────────────────

interface SettingsNavProps {
  activeTab: TabId;
  onSelect: (tab: TabId) => void;
  tabs: TabDef[];
}

/**
 * Responsive behavior:
 * - Mobile: horizontal scroll “pill” nav
 * - Desktop: left vertical nav
 */
function SettingsNav({ activeTab, onSelect, tabs }: SettingsNavProps) {
  return (
    <>
      {/* Mobile nav */}
      <nav className='md:hidden -mx-4 sm:-mx-6 lg:mx-0 px-4 sm:px-6 lg:px-0 overflow-x-auto'>
        <div className='flex gap-2 pb-2'>
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                type='button'
                onClick={() => !tab.soon && onSelect(tab.id)}
                disabled={tab.soon}
                className={`shrink-0 inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold transition ${
                  isActive
                    ? 'bg-orange-50 text-orange-700 border-orange-200'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                } ${tab.soon ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <Icon className={`w-4 h-4 ${isActive ? 'text-orange-600' : 'text-slate-400'}`} />
                <span className={`whitespace-nowrap ${isActive ? 'inline-block' : 'hidden md:inline-block'}`}>
                  {tab.label}
                </span>
                {tab.soon && (
                  <span className='text-[10px] font-bold uppercase tracking-wide bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded'>
                    bientot
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Desktop nav */}
      <nav className='hidden md:flex flex-col gap-1 w-64 shrink-0'>
        {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;

          return (
            <button
              key={tab.id}
              type='button'
              onClick={() => !tab.soon && onSelect(tab.id)}
              disabled={tab.soon}
              className={`flex items-start gap-3 w-full px-3 py-3 rounded-lg text-left transition-colors group
								${isActive ? 'bg-orange-50 text-orange-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}
								${tab.soon ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
							`}
            >
              <Icon
                className={`w-4 h-4 mt-0.5 shrink-0 ${
                  isActive ? 'text-orange-600' : 'text-slate-400 group-hover:text-slate-600'
                }`}
              />
              <div className='min-w-0'>
                <div className='flex items-center gap-2'>
                  <span className={`text-sm font-semibold ${isActive ? 'text-orange-700' : ''}`}>{tab.label}</span>
                  {tab.soon && (
                    <span className='text-[10px] font-bold uppercase tracking-wide bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded'>
                      bientot
                    </span>
                  )}
                </div>
                <p className='text-xs text-slate-400 mt-0.5 truncate'>{tab.description}</p>
              </div>
            </button>
          );
        })}
      </nav>
    </>
  );
}

// ─── Referral Panel ─────────────────────────────────────────────

function ReferralPanel() {
  const user = useAuthStore(s => s.user);
  const [copied, setCopied] = useState(false);

  function copyReferralCode() {
    if (!user?.referralCode) return;
    navigator.clipboard.writeText(user.referralCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className='space-y-6 max-w-2xl'>
      <div className='bg-white rounded-2xl p-4 sm:p-6 border border-slate-200'>
        <div className='flex items-center gap-3 mb-6'>
          <div className='w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center'>
            <Gift className='w-5 h-5 text-indigo-600' />
          </div>
          <div className='min-w-0'>
            <h3 className='font-bold text-slate-900'>Mon code parrainage</h3>
            <p className='text-sm text-slate-500 truncate'>Parrainez des joueurs et gagnez des recompenses</p>
          </div>
        </div>

        <p className='text-sm text-slate-600 mb-5 leading-relaxed max-w-2xl'>
          Partagez ce code unique avec d&apos;autres joueurs. Chaque joueur qui s&apos;inscrit avec votre code vous fait
          progresser dans les paliers de recompenses.
        </p>

        <div className='flex flex-col sm:flex-row sm:items-center gap-3 max-w-sm'>
          <input
            readOnly
            value={user?.referralCode ?? '—'}
            className='w-full px-4 py-3 border border-slate-200 rounded-xl bg-slate-50 font-mono text-xl font-extrabold tracking-widest text-slate-900 select-all cursor-text'
          />
          <button
            onClick={copyReferralCode}
            disabled={!user?.referralCode}
            title='Copier le code'
            aria-label='Copier le code de parrainage'
            className='w-full sm:w-12 cursor-pointer flex h-12 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-600 disabled:opacity-40'
          >
            {copied ? <Check className='w-5 h-5 text-emerald-500' /> : <Copy className='w-5 h-5' />}
          </button>
        </div>

        {copied && <p className='mt-2 text-sm font-semibold text-emerald-600'>Code copie dans le presse-papiers !</p>}
      </div>
    </div>
  );
}

// ─── Coming Soon Stub ───────────────────────────────────────────

function ComingSoonPanel({ tab }: { tab: TabDef }) {
  const Icon = tab.icon;
  return (
    <div className='max-w-2xl'>
      <div className='bg-white rounded-2xl p-4 sm:p-6 border border-dashed border-slate-300'>
        <div className='flex items-center gap-3 mb-4'>
          <div className='w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center'>
            <Icon className='w-5 h-5 text-slate-400' />
          </div>
          <div className='min-w-0'>
            <h3 className='font-bold text-slate-900 truncate'>{tab.label}</h3>
            <p className='text-sm text-slate-500 truncate'>{tab.description}</p>
          </div>
        </div>

        <div className='flex items-center gap-2 text-sm text-slate-500'>
          <Clock className='w-4 h-4' />
          <span>Bientot disponible</span>
        </div>
      </div>
    </div>
  );
}

// ─── Privacy Panel ────────────────────────────────────────────

function PrivacyPanel() {
  const user = useAuthStore(s => s.user);
  const updateUser = useAuthStore(s => s.updateUser);

  const isProfilePublic = user?.isProfilePublic ?? true;

  const toggleMutation = useMutation({
    mutationFn: (value: boolean) => updateMeApi({ isProfilePublic: value }),
    onSuccess: (_data, value) => {
      updateUser({ isProfilePublic: value });
      toast.success('Preferences mises à jour');
    },
    onError: (err: Error) => {
      toast.error(err.message ?? 'Erreur lors de la mise à jour');
    },
  });

  return (
    <div className='space-y-6 max-w-2xl'>
      <div className='bg-white rounded-2xl p-4 sm:p-6 border border-slate-200'>
        <div className='flex items-center gap-3 mb-6'>
          <div className='w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center'>
            {isProfilePublic ? (
              <Eye className='w-5 h-5 text-orange-600' />
            ) : (
              <EyeOff className='w-5 h-5 text-slate-400' />
            )}
          </div>
          <div className='min-w-0'>
            <h3 className='font-bold text-slate-900'>Visibilite du profil</h3>
            <p className='text-sm text-slate-500 truncate'>Controlez la visibilite de votre profil joueur</p>
          </div>
        </div>

        <div className='space-y-4'>
          <div className='flex items-center justify-between gap-4 p-4 bg-slate-50 rounded-xl'>
            <div className='flex-1 min-w-0'>
              <div className='flex items-center gap-2'>
                <span className='font-semibold text-sm text-slate-900'>Profil joueur public</span>
                <span
                  className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                    isProfilePublic ? 'bg-orange-100 text-orange-700' : 'bg-slate-200 text-slate-500'
                  }`}
                >
                  {isProfilePublic ? 'visible' : 'masque'}
                </span>
              </div>
              <p className='text-xs text-slate-500 mt-1'>
                {isProfilePublic
                  ? 'Votre profil, statistiques et historique sont visibles par les autres joueurs connectes.'
                  : 'Votre profil est masque. Les autres joueurs voient un message "profil prive".'}
              </p>
            </div>

            <button
              type='button'
              onClick={() => toggleMutation.mutate(!isProfilePublic)}
              disabled={toggleMutation.isPending}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50 ${
                isProfilePublic ? 'bg-orange-500' : 'bg-slate-300'
              }`}
              aria-label='Basculer la visibilite du profil'
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  isProfilePublic ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Player Subscription Panel ────────────────────────────────────

function PlayerSubscriptionPanel() {
  const user = useAuthStore(s => s.user);
  const playerPlusEnabled = useSaasStore(s => s.playerPlusEnabled);
  const isSubscribed = isUserPremium(user?.userPlan);

  if (!playerPlusEnabled) {
    return (
      <div className='max-w-2xl'>
        <div className='bg-white rounded-xl border border-slate-200 p-6 sm:p-8 text-center'>
          <div className='w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-4'>
            <Clock className='w-6 h-6 text-amber-600' />
          </div>
          <span className='inline-flex items-center gap-1.5 bg-amber-100 text-amber-700 text-xs font-bold px-3 py-1.5 rounded-full mb-4'>
            <Clock className='w-3 h-3' />
            Bientôt disponible
          </span>
          <h3 className='text-lg font-bold text-slate-900 mt-3'>Joueur Plus</h3>
          <p className='text-sm text-slate-500 mt-2 max-w-md mx-auto'>
            L&apos;offre d&apos;abonnement Joueur Plus sera disponible prochainement. Vous serez notifie des sa mise en
            ligne.
          </p>
        </div>
      </div>
    );
  }

  const planConfig: SubscriptionPlanConfig = {
    planName: 'Joueur Plus',
    description:
      "Debloquez des statistiques avancees, la priorite d'inscription aux concours, et un profil personnalise.",
    priceMonthly: `${JOUEUR_PLUS_MONTHLY_TTC_CENTS / 100}`,
    priceAnnual: `${JOUEUR_PLUS_ANNUAL_TTC_CENTS / 100}`,
    features: [
      'Statistiques detaillees et historique complet',
      "Priorite d'inscription aux concours populaires",
      'Badge Joueur Plus sur votre profil',
      'Notifications avancees et alertes personnalisees',
    ],
    isSubscribed,
    onSetupIntent: () => userStripeSetupIntentApi(),
    onSubscribe: (interval, paymentMethodId) => userStripeSubscribeApi(interval, paymentMethodId),
    onBillingPortal: isSubscribed ? () => userStripeBillingPortalApi() : null,
    accentColor: 'orange',
  };

  return <SubscriptionPanel config={planConfig} />;
}

// ─── Panel content ─────────────────────────────────────────────

function TabContent({ tab }: { tab: TabId }) {
  switch (tab) {
    case 'security':
      return <SecuritySettingsPanel />;
    case 'subscription':
      return <PlayerSubscriptionPanel />;
    case 'referral':
      return <ReferralPanel />;
    case 'notifications':
      return <NotificationPreferencesPanel categories={PLAYER_NOTIFICATION_CATEGORIES} accentColor='orange' />;
    case 'gdpr':
      return <GdprSettingsPanel accentColor='orange' />;
    case 'privacy':
      return <PrivacyPanel />;
    default: {
      const tabDef = TABS.find(t => t.id === tab);
      return tabDef ? <ComingSoonPanel tab={tabDef} /> : null;
    }
  }
}

// ─── Main ──────────────────────────────────────────────────────

export default function PlayerSettings() {
  const referralEnabled = useSaasStore(s => s.referralEnabled);
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab') as TabId | null;

  const tabs = referralEnabled ? TABS : TABS.filter(t => t.id !== 'referral');
  const activeTab: TabId = rawTab && tabs.some(t => t.id === rawTab) ? rawTab : DEFAULT_TAB;

  function handleSelect(tab: TabId) {
    setSearchParams({ tab }, { replace: true });
  }

  const currentTab = tabs.find(t => t.id === activeTab)!;

  return (
    <DashboardLayout sidebarConfig={playerSidebar}>
      <div className='p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto'>
        <PageHeader title='Preferences' subtitle='Personnalisez votre experience et securisez votre compte' />

        {/* Layout responsive:
					- Mobile: nav horizontal (scroll) + content stacked
					- Desktop: left nav + vertical divider + content
				*/}
        <div className='mt-6 flex flex-col md:flex-row md:gap-8'>
          {/* Nav */}
          <div className='md:shrink-0'>
            <SettingsNav activeTab={activeTab} onSelect={handleSelect} tabs={tabs} />
          </div>

          {/* Divider desktop only */}
          <div className='hidden md:block w-px bg-slate-200 self-stretch' />

          {/* Content */}
          <div className='flex-1 min-w-0 mt-4 md:mt-0'>
            <div className='mb-6'>
              <h2 className='text-xl font-bold text-slate-900'>{currentTab.label}</h2>
              <p className='text-sm text-slate-500 mt-0.5'>{currentTab.description}</p>
            </div>

            <TabContent tab={activeTab} />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
